import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Plus, Search, MoreVertical, Copy, Trash2, ExternalLink,
  Eye, MousePointer, TrendingUp, FileText, Settings
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function Dashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const [proposalsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/proposals`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProposals(proposalsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (proposalId) => {
    try {
      await axios.post(`${API}/proposals/${proposalId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Proposal duplicated");
      fetchData();
    } catch (error) {
      toast.error("Failed to duplicate");
    }
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await axios.delete(`${API}/proposals/${proposalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const filteredProposals = proposals.filter(p =>
    p.hotel_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.proposal_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-1" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-white/50">Manage hotel proposals</p>
        </div>
        <button
          onClick={() => navigate("/proposals/new")}
          className="btn-accent flex items-center gap-2"
          data-testid="create-proposal-btn"
        >
          <Plus className="w-5 h-5" />
          New Proposal
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: FileText, label: "Proposals", value: analytics.total_proposals, color: "from-[#C9A96E]/20 to-purple-600/20" },
            { icon: Eye, label: "Views", value: analytics.total_views, color: "from-blue-500/20 to-cyan-600/20" },
            { icon: MousePointer, label: "Conversions", value: analytics.total_conversions, color: "from-emerald-500/20 to-teal-600/20" },
            { icon: TrendingUp, label: "Rate", value: `${analytics.conversion_rate}%`, color: "from-amber-500/20 to-orange-600/20" }
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i }}
              className={`glass-card-static p-5 bg-gradient-to-br ${stat.color}`}
              data-testid={`stat-${stat.label.toLowerCase()}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-white/80" />
                </div>
              </div>
              <p className="font-mono text-2xl text-white">{stat.value}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-12"
            data-testid="search-proposals-input"
          />
        </div>
      </div>

      {/* Proposals Table */}
      <div className="glass-card-static overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="font-display text-xl text-white mb-2">No proposals yet</h3>
            <p className="text-white/40 mb-6">Create your first proposal</p>
            <button
              onClick={() => navigate("/proposals/new")}
              className="btn-accent inline-flex items-center gap-2"
              data-testid="empty-state-create-btn"
            >
              <Plus className="w-5 h-5" />
              New Proposal
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Proposal ID</th>
                  <th>Hotel</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Conversions</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map((proposal, index) => (
                  <motion.tr
                    key={proposal.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    data-testid={`proposal-row-${proposal.id}`}
                  >
                    <td>
                      <span className="font-mono text-[#C9A96E] text-sm">{proposal.proposal_id}</span>
                    </td>
                    <td>
                      <div>
                        <p className="text-white font-medium">{proposal.hotel_name}</p>
                        <p className="text-white/30 text-xs">{proposal.location || "\u2014"}</p>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${proposal.status}`}>
                        {proposal.status}
                      </span>
                    </td>
                    <td className="font-mono text-white/60 text-sm">{proposal.views}</td>
                    <td className="font-mono text-white/60 text-sm">{proposal.conversions}</td>
                    <td className="text-white/40 text-sm">
                      {new Date(proposal.updated_at).toLocaleDateString()}
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button 
                            className="p-2 hover:bg-white/5 rounded-lg"
                            data-testid={`proposal-actions-${proposal.id}`}
                          >
                            <MoreVertical className="w-5 h-5 text-white/40" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#0A0A0A]/95 backdrop-blur-xl border-white/10">
                          <DropdownMenuItem
                            onClick={() => navigate(`/proposals/${proposal.id}/edit`)}
                            className="text-white hover:bg-white/5 cursor-pointer"
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => window.open(`/p/${proposal.proposal_id}`, '_blank')}
                            className="text-white hover:bg-white/5 cursor-pointer"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDuplicate(proposal.id)}
                            className="text-white hover:bg-white/5 cursor-pointer"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(proposal.id)}
                            className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
